import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'

import {
  getLedger,
  getWallet,
  listApiTokens,
  listApplications,
  listPermissions,
  listRequests,
} from '../../api/portal'
import { Fact } from '../../components/ui/Fact'
import { SectionCard } from '../../components/ui/SectionCard'
import { StatusChip } from '../../components/ui/StatusChip'
import { can } from '../../auth/session'
import { usePortal } from '../../auth/sessionContext'
import { GettingStarted } from './GettingStarted'
import { requestMoney } from '../requests/money'
import { UNSET, formatCount, formatDateTime, formatIrr } from '../../format'
import { ledgerEntryLabel, roleLabel } from '../../vocabulary'

/** A quiet line of «label — value», for the lists that are not tables. */
function Row({
  when,
  what,
  status,
  amount,
}: {
  when: string
  what: React.ReactNode
  status?: string
  amount?: string
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      // Not wrapping. A row that wraps drops the amount onto a line of its
      // own, where it reads as belonging to the row beneath it.
      sx={{ alignItems: 'center', py: 0.75, minWidth: 0 }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }}
      >
        {when}
      </Typography>
      <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }}>{what}</Box>
      {status ? (
        <Box sx={{ flexShrink: 0 }}>
          <StatusChip status={status} />
        </Box>
      ) : null}
      {amount ? (
        <Typography
          variant="body2"
          noWrap
          sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, flexShrink: 0 }}
        >
          {amount}
        </Typography>
      ) : null}
    </Stack>
  )
}

/**
 * The home page, which is to say the five answers.
 *
 * It used to open with an onboarding checklist that filled the first screen,
 * and the facts a returning customer came for — am I active, what is my
 * balance, is my API working, what happened lately — began below it. A
 * checklist is scaffolding: useful once, in the way every day after.
 *
 * So the answers come first, and the checklist is rendered underneath and
 * only while something on it is still outstanding.
 */
export function DashboardPage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const seesWallet = can(membership, 'view_wallet')
  const seesLedger = can(membership, 'view_ledger')

  const wallet = useQuery({
    queryKey: ['client', 'wallet', customerId],
    queryFn: ({ signal }) => getWallet(customerId, signal),
    enabled: seesWallet,
  })
  const applications = useQuery({
    queryKey: ['client', 'applications', customerId],
    queryFn: ({ signal }) => listApplications(customerId, signal),
  })
  const permissions = useQuery({
    queryKey: ['client', 'permissions', customerId],
    queryFn: ({ signal }) => listPermissions(customerId, signal),
  })
  const requests = useQuery({
    queryKey: ['client', 'requests', customerId, 1, '', ''],
    queryFn: ({ signal }) => listRequests(customerId, 1, {}, signal),
  })
  const ledger = useQuery({
    queryKey: ['client', 'ledger', customerId],
    queryFn: ({ signal }) => getLedger(customerId, 1, signal),
    enabled: seesLedger,
  })

  const apps = applications.data?.results ?? []
  const primary = apps[0]
  // One application's tokens, not every application's. A dashboard that makes
  // a request per application gets slower the more successful the customer
  // becomes, and the answer it needs — "can I call the API at all" — is the
  // same one either way.
  const tokens = useQuery({
    queryKey: ['client', 'api-tokens', primary?.id],
    queryFn: ({ signal }) => listApiTokens(customerId, primary!.id, signal),
    enabled: primary !== undefined && can(membership, 'view_applications'),
  })
  const liveTokens = (Array.isArray(tokens.data) ? tokens.data : []).filter(
    (token) => token.status === 'active',
  )

  const enabledServices = (permissions.data?.results ?? []).filter((p) => p.is_enabled).length
  const recent = (requests.data?.results ?? []).slice(0, 5)
  const movements = (ledger.data?.results ?? []).slice(0, 4)
  const held = wallet.data?.held_balance_irr ?? 0
  const lastRequest = requests.data?.results?.[0]

  const onboarding = {
    hasApplication: apps.length > 0,
    hasAccess: enabledServices > 0,
    hasCredential: liveTokens.length > 0,
    // Only a positive balance counts: an empty wallet is not a funded one, and
    // the gateway refuses the call either way.
    hasFunds: (wallet.data?.available_balance_irr ?? 0) > 0,
    hasRequests: (requests.data?.count ?? 0) > 0,
  }
  const settled = Object.values(onboarding).every(Boolean)

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h1" gutterBottom>
          {membership.customer.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          شما با نقش <strong>{roleLabel(membership.role)}</strong> وارد شده‌اید.
        </Typography>
      </Box>

      <Stack spacing={3}>
        <SectionCard title="وضعیت حساب">
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact label="حساب">
                <StatusChip status={membership.customer.status} />
              </Fact>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact
                label="اپلیکیشن"
                hint={primary ? primary.name : 'اپلیکیشن‌ها را ما می‌سازیم.'}
              >
                {primary ? <StatusChip status={primary.status} /> : '—'}
              </Fact>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact
                label="توکن API"
                hint={
                  liveTokens.length > 0
                    ? `${formatCount(liveTokens.length)} توکن فعال`
                    : 'هنوز دریافت نشده است.'
                }
              >
                <StatusChip status={liveTokens.length > 0 ? 'active' : 'inactive'} />
              </Fact>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Fact label="سرویس‌های فعال" hint="آنچه اپلیکیشن شما اجازهٔ فراخوانی دارد.">
                {permissions.data ? formatCount(enabledServices) : UNSET.amount}
              </Fact>
            </Grid>
          </Grid>
        </SectionCard>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="کیف پول">
              {seesWallet ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" component="p" color="text.secondary">
                      موجودی قابل استفاده
                    </Typography>
                    <Typography
                      component="p"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        fontSize: 'clamp(1.5rem, 3vw, 2.125rem)',
                        lineHeight: 1.3,
                      }}
                    >
                      {wallet.data ? formatIrr(wallet.data.available_balance_irr) : UNSET.amount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {/* Held money is invisible arithmetic otherwise: a
                          balance lower than the last recharge, with nothing
                          explaining the difference, reads as money gone. */}
                      {held > 0
                        ? `${formatIrr(held)} برای درخواست‌های در جریان رزرو شده است.`
                        : 'فراخوانی بعدی از این مبلغ پرداخت می‌شود.'}
                    </Typography>
                  </Box>

                  {seesLedger ? (
                    <>
                      <Divider />
                      {movements.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          هنوز تراکنشی برای این حساب ثبت نشده است. شارژ حساب از سمت ما انجام
                          می‌شود.
                        </Typography>
                      ) : (
                        <Box>
                          {movements.map((entry) => (
                            <Row
                              key={entry.id}
                              when={formatDateTime(entry.created_at)}
                              what={
                                <Typography variant="body2" noWrap>
                                  {ledgerEntryLabel(entry.entry_type)}
                                </Typography>
                              }
                              amount={formatIrr(entry.amount_irr)}
                            />
                          ))}
                          <Box sx={{ pt: 1 }}>
                            <Link to="/wallet">گردش مالی کامل</Link>
                          </Box>
                        </Box>
                      )}
                    </>
                  ) : null}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  کیف پول برای نقش شما قابل مشاهده نیست.
                </Typography>
              )}
            </SectionCard>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="مصرف اخیر">
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Fact label="درخواست‌های ثبت‌شده">
                      {requests.data ? formatCount(requests.data.count) : UNSET.amount}
                    </Fact>
                  </Grid>
                  <Grid size={6}>
                    <Fact label="آخرین درخواست">
                      {lastRequest ? formatDateTime(lastRequest.created_at) : UNSET.never}
                    </Fact>
                  </Grid>
                </Grid>
                <Divider />
                {recent.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    هنوز درخواستی ثبت نشده است. به محض نخستین فراخوانی، نتیجه و اثر مالی آن اینجا
                    دیده می‌شود.
                  </Typography>
                ) : (
                  <Box>
                    {recent.map((row) => (
                      <Row
                        key={row.request_id}
                        when={formatDateTime(row.created_at)}
                        what={
                          <Typography variant="body2" noWrap title={row.operation_code}>
                            {row.operation_display_name_fa || row.operation_code}
                          </Typography>
                        }
                        status={row.status}
                        // The effect on the balance, not the charge: a refunded
                        // request showing its charge and not its refund reads
                        // as money the customer never got back.
                        amount={formatIrr(requestMoney(row).netIrr)}
                      />
                    ))}
                    <Box sx={{ pt: 1 }}>
                      <Link to="/requests">همهٔ درخواست‌ها</Link>
                    </Box>
                  </Box>
                )}
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>

        {/* Only what this customer may actually do. Funding, granting access
            and creating applications are all operator actions on this
            platform, and a button for one of them would be a promise the
            portal cannot keep. */}
        <SectionCard title="کارهای رایج">
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {can(membership, 'view_applications') ? (
              <Button component={Link} to="/connection" variant="contained">
                {liveTokens.length > 0 ? 'مدیریت توکن API' : 'دریافت توکن API'}
              </Button>
            ) : null}
            <Button component={Link} to="/docs" variant="outlined">
              مشاهدهٔ مستندات
            </Button>
            {can(membership, 'view_requests') ? (
              <Button component={Link} to="/requests" variant="outlined">
                مشاهدهٔ درخواست‌ها
              </Button>
            ) : null}
          </Stack>
        </SectionCard>

        {settled ? null : <GettingStarted state={onboarding} />}
      </Stack>
    </Box>
  )
}
